import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { STONES } from "@/data/stones";

const [quartz, italian, indian, granite, impOnyx, impGranite, natOnyx] = STONES;

function Tile({ stone, className = "", center = false }) {
  return (
    <Link
      to="/collections"
      data-testid={`grid-tile-${stone.slug}`}
      className={`group relative block overflow-hidden ${className}`}
    >
      <img
        src={stone.image}
        alt={stone.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent transition-opacity duration-500 group-hover:from-black/70" />
      <div
        className={
          center
            ? "absolute inset-0 flex flex-col items-center justify-center text-center px-3"
            : "absolute bottom-0 left-0 p-4 md:p-5"
        }
      >
        <p className="text-white/70 text-[10px] tracking-[0.3em] mb-1">{stone.number}</p>
        <p className="font-serif text-white text-lg md:text-2xl font-light tracking-tight leading-tight">
          {stone.name}
        </p>
      </div>
    </Link>
  );
}

function TextTile({ dark = false, className = "", children }) {
  return (
    <div
      className={`relative flex flex-col justify-center p-5 md:p-6 ${
        dark ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function EnquireTile({ className = "" }) {
  return (
    <Link
      to="/contact"
      data-testid="grid-tile-enquire"
      className={`group relative flex flex-col justify-center p-5 md:p-6 bg-primary text-primary-foreground ${className}`}
    >
      <p className="text-[10px] tracking-[0.3em] uppercase text-primary-foreground/50 mb-1">Your project</p>
      <p className="font-serif text-lg md:text-xl font-light italic flex items-center gap-2">
        Enquire
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </p>
    </Link>
  );
}

const MosaicGrid = ({ grout = false, centerText = false }) => (
  <div
    className={`grid grid-cols-2 md:grid-cols-4 auto-rows-[130px] sm:auto-rows-[150px] lg:auto-rows-[180px] grid-flow-dense ${
      grout ? "gap-[3px] bg-[#E4E0D6] p-[3px]" : "gap-0"
    }`}
  >
    <Tile stone={quartz} center={centerText} className="col-span-2 row-span-2" />
    <Tile stone={italian} center={centerText} className="col-span-1 row-span-1 md:col-span-2" />
    <Tile stone={granite} center={centerText} className="col-span-1 row-span-1 md:row-span-2" />
    <Tile stone={indian} center={centerText} />
    <Tile stone={impOnyx} center={centerText} />
    <Tile stone={impGranite} center={centerText} className="col-span-2 row-span-1" />
    <Tile stone={natOnyx} center={centerText} className="col-span-1 row-span-1 md:row-span-2" />
    <TextTile className="col-span-1 row-span-1">
      <p className="font-serif text-4xl md:text-5xl font-light leading-none">07</p>
      <p className="text-[10px] tracking-[0.25em] uppercase text-secondary-foreground mt-2">Families of stone</p>
    </TextTile>
    <EnquireTile className="col-span-1 row-span-1" />
  </div>
);

const EditorialGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-6 auto-rows-[130px] sm:auto-rows-[150px] lg:auto-rows-[170px] grid-flow-dense gap-0">
    <Tile stone={quartz} center className="col-span-2 md:col-span-3 row-span-2" />
    <Tile stone={italian} center className="col-span-1 md:col-span-3 row-span-1" />
    <Tile stone={indian} center className="col-span-1 md:col-span-2 row-span-1" />
    <Tile stone={granite} center className="col-span-1 row-span-1" />
    <Tile stone={impOnyx} center className="col-span-1 md:col-span-2 row-span-1" />
    <Tile stone={impGranite} center className="col-span-1 md:col-span-2 row-span-1" />
    <Tile stone={natOnyx} center className="col-span-1 row-span-1" />
    <EnquireTile className="col-span-1 row-span-1" />
  </div>
);

function Frame({ title, note, children }) {
  return (
    <section className="mb-24">
      <div className="mb-6">
        <h2 className="font-serif text-3xl font-light tracking-tight">{title}</h2>
        <p className="mt-2 text-sm font-light text-muted-foreground max-w-xl">{note}</p>
      </div>
      <div className="border border-foreground/20 overflow-hidden">{children}</div>
    </section>
  );
}

export default function GridOptions() {
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-40 pb-32" data-testid="grid-options-page">
      <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">
        Preview — not linked in the menu
      </p>
      <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tighter leading-[0.98] mb-6">
        The <span className="italic">inlay</span> grid.
      </h1>
      <p className="max-w-lg text-base font-light leading-relaxed text-muted-foreground mb-20">
        Three ways to re-cut the collections grid so the slabs connect like marble inlay —
        names overlaid on the stone. Tell me which one and I'll replace the current grid.
      </p>

      <Frame
        title="Option 1 — Seamless inlay"
        note="Zero gaps: every slab touches the next, like a book-matched marble floor. Names sit on the stone, bottom-left."
      >
        <MosaicGrid />
      </Frame>

      <Frame
        title="Option 2 — Hairline grout"
        note="Same mosaic, but separated by 3px stone-toned hairlines — the metal-strip look of traditional inlay work."
      >
        <MosaicGrid grout />
      </Frame>

      <Frame
        title="Option 3 — Editorial mosaic"
        note="A wider 6-column rhythm with centered names and larger statement pieces — more gallery, more drama."
      >
        <EditorialGrid />
      </Frame>
    </div>
  );
}
